import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { AlertCircleIcon } from '@untitledui-icons/react/line'

import {
  useUpdateWidgetVenueAddress,
  type WidgetTemplateAssignment,
  type WidgetTemplateKind,
} from '@/hooks/api/useWidgets'
import { WidgetInlineNote, WidgetToggleCard } from './WidgetEditorControls'
import { WidgetVenueAddressField } from './WidgetVenueAddressField'


interface WidgetMultiVenueCardProps {
  kind: WidgetTemplateKind
  onKindChange: (kind: WidgetTemplateKind) => void
  assignments: WidgetTemplateAssignment[]
}

/**
 * Turns the location picker on for this template. Each assigned venue's address
 * is filled from its Google place and can be overridden here. Addresses save
 * immediately — they belong to the assistant, not to the styling document.
 */
export function WidgetMultiVenueCard({
  kind,
  onKindChange,
  assignments,
}: WidgetMultiVenueCardProps) {
  const { t } = useTranslation()
  const base = 'resources.widgets.multi_venue'

  const updateAddress = useUpdateWidgetVenueAddress()
  const isMulti = kind === 'multi'

  const handleSave = async (assistantId: number, address: string | null) => {
    try {
      await updateAddress.mutateAsync({ assistant_id: assistantId, address })
    } catch {
      toast.error(t(`${base}.save_failed_toast`))
    }
  }

  const handleKindChange = (nextIsMulti: boolean) => {
    onKindChange(nextIsMulti ? 'multi' : 'single')
  }

  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-lg leading-7 font-semibold text-gray-900 dark:text-gray-100">
        {t(`${base}.title`)}
      </h2>

      <WidgetToggleCard
        title={t(`${base}.toggle_title`)}
        description={t(`${base}.toggle_description`)}
        isSelected={isMulti}
        onChange={handleKindChange}
      >
        {isMulti &&
          (assignments.length === 0 ? (
            <WidgetInlineNote icon={<AlertCircleIcon />}>{t(`${base}.empty`)}</WidgetInlineNote>
          ) : (
            <div className="mt-4 flex flex-col gap-3">
              {assignments.map((assignment) => (
                <WidgetVenueAddressField
                  key={assignment.assistant_id}
                  label={assignment.assistant_name}
                  value={assignment.address}
                  isSaving={
                    updateAddress.isPending &&
                    updateAddress.variables?.assistant_id === assignment.assistant_id
                  }
                  onSave={(address) => void handleSave(assignment.assistant_id, address)}
                />
              ))}
            </div>
          ))}
      </WidgetToggleCard>
    </section>
  )
}
